import type { Ref } from '@nuxtjs/composition-api';
import { onMounted, onUnmounted, ref } from '@nuxtjs/composition-api';
import debounce from 'lodash.debounce';

export function useLinesCounter(elementRef: Ref<HTMLElement | null> | null) {
  const linesCount = ref(0);
  let resizeObserver: ResizeObserver | null = null;

  const updateLinesCount = () => {
    const element = elementRef.value;
    if (!element) {
      linesCount.value = 0;
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(element);
    const rects = range.getClientRects();

    if (rects.length === 0) {
      linesCount.value = 0;
      return;
    }
    const uniqueTopPositions = new Set<number>();
    for (let i = 0; i < rects.length; i++) {
      uniqueTopPositions.add(rects[i].top);
    }
    linesCount.value = uniqueTopPositions.size;
  };

  const DEBOUNCE_DELAY = 200;
  const debouncedUpdateLinesCount = debounce(updateLinesCount, DEBOUNCE_DELAY);

  onMounted(() => {
    if (!elementRef?.value) return;

    updateLinesCount();

    resizeObserver = new ResizeObserver(() => {
      debouncedUpdateLinesCount();
    });
    resizeObserver.observe(elementRef.value);

    window.addEventListener('resize', debouncedUpdateLinesCount);
  });

  onUnmounted(() => {
    if (resizeObserver && elementRef.value) {
      resizeObserver.unobserve(elementRef.value);
      resizeObserver.disconnect();
    }
    window.removeEventListener('resize', debouncedUpdateLinesCount);

    debouncedUpdateLinesCount.cancel();
  });

  return {
    linesCount
  };
}
